import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CreateDonationRequest, PaymentMethod } from '@/types';

type DonationFormData = {
  // amount non è più parte del form data di react-hook-form
  paymentMethod: PaymentMethod;
  notes?: string;
};

const donationSchema = yup.object({
  // Rimuoviamo la validazione di amount da yup, la gestiamo noi
  paymentMethod: yup
    .string()
    .oneOf(['CASH', 'CARD', 'BANK_TRANSFER', 'CHECK', 'OTHER'])
    .required('Metodo di pagamento obbligatorio'),
  notes: yup
    .string()
    .max(1000, 'Note troppo lunghe (max 1000 caratteri')
    .optional(),
});

interface DonationDetailsFormProps {
  data: CreateDonationRequest;
  onChange: (data: Partial<CreateDonationRequest>) => void;
  onNext: () => void;
  onBack: () => void;
  disabled: boolean;
}

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Contanti' },
  { value: 'CARD', label: 'Carta di credito/debito' },
  { value: 'BANK_TRANSFER', label: 'Bonifico bancario' },
  { value: 'CHECK', label: 'Assegno' },
  { value: 'OTHER', label: 'Altro' },
];

const QUICK_AMOUNTS = [10, 25, 50, 100, 250, 500];

export const DonationDetailsForm: React.FC<DonationDetailsFormProps> = ({
  data,
  onChange,
  onNext,
  onBack,
  disabled,
}) => {
  // Gestione diretta dell'importo senza react-hook-form
  const [amount, setAmount] = useState<number>(data.amount || 0);
  const [amountError, setAmountError] = useState<string>('');

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<DonationFormData>({
    resolver: yupResolver(donationSchema),
    defaultValues: {
      paymentMethod: data.paymentMethod,
      notes: data.notes,
    },
    mode: 'onChange',
  });

  // Aggiorna il form quando l'importo cambia
  useEffect(() => {
    console.log('Amount changed:', { amount, type: typeof amount });
    onChange({ amount });
  }, [amount, onChange]);

  const handleFormChange = (field: keyof DonationFormData, value: any) => {
    setValue(field, value);
    onChange({ [field]: value });
  };

  const handleQuickAmount = (quickAmount: number) => {
    setAmount(quickAmount);
    setAmountError('');
  };

  const onSubmit = (formData: DonationFormData) => {
    // Usa il nostro stato locale per l'importo
    const cleanFormData = {
      ...formData,
      amount: amount
    };
    console.log('Submitting form data:', { 
      cleanFormData, 
      amount, 
      amountType: typeof amount
    });
    onChange(cleanFormData);
    onNext();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Dettagli Donazione
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Importo *
          </Typography>
          
          <TextField
            fullWidth
            label="Importo"
            type="number"
            inputProps={{ step: 0.01, min: 0.01, max: 999999.99 }}
            error={!!amountError}
            helperText={amountError || 'Inserisci l\'importo della donazione'}
            disabled={disabled}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
            value={amount === 0 ? '' : amount}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setAmount(0);
                setAmountError('');
              } else {
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue) && numericValue > 0) {
                  setAmount(numericValue);
                  setAmountError('');
                } else if (numericValue === 0) {
                  setAmount(0);
                  setAmountError('');
                } else {
                  setAmountError('Inserisci un importo valido');
                }
              }
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value === '') {
                setAmount(0);
                setAmountError('');
              } else {
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue) && numericValue > 0) {
                  setAmount(numericValue);
                  setAmountError('');
                } else {
                  setAmount(0);
                  setAmountError('Inserisci un importo valido');
                }
              }
            }}
          />

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Importi rapidi:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {QUICK_AMOUNTS.map((quickAmount) => (
              <Button
                key={quickAmount}
                variant={amount === quickAmount ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleQuickAmount(quickAmount)}
                disabled={disabled}
              >
                €{quickAmount}
              </Button>
            ))}
          </Box>
        </Box>

        <Controller
          name="paymentMethod"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.paymentMethod} sx={{ mb: 3 }}>
              <InputLabel>Metodo di pagamento *</InputLabel>
              <Select
                {...field}
                label="Metodo di pagamento *"
                disabled={disabled}
                onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
              >
                {PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.paymentMethod && (
                <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                  {errors.paymentMethod.message}
                </Typography>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Note (opzionale)"
              multiline
              rows={3}
              error={!!errors.notes}
              helperText={
                errors.notes?.message || 
                `${field.value?.length || 0}/1000 caratteri`
              }
              disabled={disabled}
              sx={{ mb: 3 }}
              onChange={(e) => handleFormChange('notes', e.target.value)}
            />
          )}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={disabled}
            size="large"
          >
            Indietro
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || disabled || amount <= 0 || !!amountError}
            size="large"
          >
            Avanti
          </Button>
        </Box>
      </form>
    </Box>
  );
};
